<template>
  <div class="flex gap-4" :data-test="`protocollink-${element.name}`">
    <div class="flex-grow">
      <DetailsListEntry
        class="mb-0 mt-3 mx-0"
        :custom-content-styles="{ 'bg-error_bg': invalid }"
        :title="element.label"
        :content="readModeModel"
      />
    </div>

    <div
      v-if="linkedProtocol && edit"
      class="flex flex-grow-0 h-10 w-10 bg-primary justify-center items-center self-end rounded cursor-pointer"
    >
      <Icon
        data-test="icon-takeover"
        name="action/takeOver"
        color="white"
        @click.native.stop="showTakeoverDataModal = true"
      />
    </div>

    <div
      v-if="linkedProtocol"
      class="flex flex-grow-0 h-10 w-10 bg-primary justify-center items-center self-end rounded cursor-pointer"
      @click="linkedProtocolModalOpen = true"
    >
      <Icon data-test="icon-eye" name="eye/on" color="white" />
    </div>
    <div
      v-if="edit"
      class="flex flex-grow-0 h-10 w-10 bg-primary justify-center items-center self-end rounded cursor-pointer"
      @click="changeLinkModalOpen = true"
    >
      <Icon data-test="icon-link" name="action/link" color="white" />
    </div>
    <Modal
      v-if="showTakeoverDataModal"
      data-test-id="take-over"
      is-open
      :title="$tc('protocol.take_over_data')"
      :max-width="650"
      @cancel="showTakeoverDataModal = false"
      @confirm="applyTakeOverData"
    />

    <Modal
      v-if="linkedProtocol && linkedProtocolModalOpen"
      data-test-id="linked-protocol"
      is-open
      :title="$tc('protocol.linking.linked_protocol', undefined, { field: element.label })"
      :max-width="800"
      view-only
      @cancel="linkedProtocolModalOpen = false"
    >
      <ProtocolDetails :protocol="linkedProtocol" one-column-layout />
    </Modal>
    <FormProtocolLinkDialog
      v-if="edit && changeLinkModalOpen"
      :element="element"
      @link="model = $event"
      @cancel="changeLinkModalOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { isProtocolLink, logger, Protocol, ProtocolLink, ProtocolLinkElement } from '@anschuetz-elog/common';
import { cloneDeep } from 'lodash';
import { computed, defineComponent, PropType, ref, toRef } from 'vue';

import DetailsListEntry from '#/components/DetailsListEntry.vue';
import Icon from '#/components/Icon.vue';
import Modal from '#/components/Modal.vue';
import ProtocolDetails from '#/components/protocol/ProtocolDetails.vue';
import useGet from '#/compositions/useGet';

import FormProtocolLinkDialog from './FormProtocolLinkDialog.vue';
import { getProtocolLinkValue } from './utils';

export default defineComponent({
  name: 'FormProtocolLink',

  components: {
    DetailsListEntry,
    Modal,
    ProtocolDetails,
    Icon,
    FormProtocolLinkDialog,
  },

  props: {
    element: {
      type: Object as PropType<ProtocolLinkElement>,
      required: true,
    },

    value: {
      type: Object as PropType<Record<string, unknown>>,
      required: true,
    },

    edit: {
      type: Boolean,
    },

    invalid: {
      type: Boolean,
    },
  },

  emits: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    input: (_value: Record<string, unknown>) => true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    invalid: (_invalid: boolean) => true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    'protocollink:takeover-data': (_linkedProtocol: Protocol) => true,
  },

  setup(props, { emit }) {
    const value = toRef(props, 'value');
    const element = toRef(props, 'element');

    const model = computed<ProtocolLink | undefined>({
      get() {
        const v = value.value[element.value.name];
        if (v !== undefined && !isProtocolLink(v)) {
          logger.error(
            'Value does not fit to protocol link element',
            'value:',
            cloneDeep(v),
            'element:',
            cloneDeep(element.value),
          );
          return undefined;
        }
        return v;
      },
      set(newElementValue) {
        emit('input', { ...value.value, [element.value.name]: newElementValue });
      },
    });

    const linkedProtocolModalOpen = ref(false);
    const changeLinkModalOpen = ref(false);
    const showTakeoverDataModal = ref(false);

    const protocolId = computed(() => (model.value !== undefined ? model.value.protocol._id : undefined));
    const { data: linkedProtocol } = useGet('protocol', protocolId);

    const readModeModel = computed(() => {
      return getProtocolLinkValue(model.value);
    });

    function applyTakeOverData(): void {
      if (linkedProtocol.value !== undefined) {
        emit('protocollink:takeover-data', linkedProtocol.value);
      }
      showTakeoverDataModal.value = false;
    }

    return {
      model,
      protocolId,
      linkedProtocol,
      readModeModel,
      linkedProtocolModalOpen,
      changeLinkModalOpen,
      applyTakeOverData,
      showTakeoverDataModal,
    };
  },
});
</script>
